import { Divider, Stack, InputAdornment, CircularProgress, TextField } from "@mui/material";
import { ReduxState } from "ra-core";
import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import SearchIcon from '@/icons/Search';
import { PrimaryClassification } from "@/gen/api/encrypt";
import { SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { FileTagResponseDto } from "@/gen/api/securefile";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { getUserProfile } from "@/reducers/userProfile";
import { PopBaseFilter } from "../input/PopAutocompleteFilter";
import { usePopTimeRangePicker, PopTimeRangeKind, displayTimeRangeLabel } from "../input/PopTimeRangePicker";
import SearchCriteriaChips, { ChipItem } from "../input/SearchCriteriaChips";
import { useFileCataAmbientDataContext } from "./FileCataAmbientDataProvider";
import { useSecureFilesTableSearchContext } from "./SecureFilesTableSearchProvider";
import { SimpleOption2 } from "../governance/search/AmbientFacilitiesContext";


const SecureFilesTableSearchRegion: FC = () => {
  const format = useUserTimeFormat()
  const userProfile = useSelector(getUserProfile)
  const { 
    states, 
    setSearchTime, 
    setSearchPClss,
    setSearchSClss,
    setSearchTag,
    setUser,
    setMachine,
  } = useSecureFilesTableSearchContext()
  const { PopTimeRangePicker, open, states: tStates } = usePopTimeRangePicker({
    label: 'Time Range',
    onChange: ({kind, start, end}) => {
      setSearchTime(start, end)
    },
    PopoverProps: {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: 'right' },
    },
    ianaTimeZone: userProfile?.ianaTimeZone,
  })
  const criteriaChips = useMemo(() => {
    const arr = [] as ChipItem[][]
    if (states.user) {
      arr.push([{ id: `u${states.user.key}`, label: 'User', content: states.user.text, delete: () => setUser(null) }])
    }
    if (states.machine) {
      arr.push([{ id: `machine`, label: 'Computer', content: states.machine.text, delete: () => setMachine(null) }])
    }
    if (states.primaryClassifications.length > 0 || states.secondaryClassifications.length > 0) {
      arr.push([
        ...(states.primaryClassifications.map(p => ({
          id: `p${p}`,
          label: 'Classification',
          content: PrimaryClassification[p],
          delete: () => setSearchPClss(p, true),
        }))),
        ...(states.secondaryClassifications.map(s => ({
          id: `s${s.secondaryClassificationId}`,
          label: 'Classification',
          content: `${s.primaryClassificationText} - ${s.name}`,
          delete: () => setSearchSClss(s, true),
        })))
      ])
    }
    if (states.tags.length > 0) {
      arr.push(states.tags.map(t => ({
        id: `t${t.tagId}`,
        label: 'Tag',
        content: t.name!,
        delete: () => setSearchTag(t, true),
      })))
    }
    if (tStates) {
      if (tStates.start || tStates.end) {
        arr.push([{
          id: 'tr',
          label: 'Time Range',
          content: 
            tStates.kind !== PopTimeRangeKind.Custom
            ? (<code style={{ textTransform: 'capitalize', marginLeft: '8px' }}>within {displayTimeRangeLabel(tStates.kind)}</code> as any)
            : !tStates.end ? `Starts from ${format(tStates.start!, 'Pp')}`
            : !tStates.start ? `Up until ${format(tStates.end!, 'Pp')}`
            : `${format(tStates.start, 'Pp')} - ${format(tStates.end, 'Pp')}`
            ,
          delete: () => open(),
        }])
      }
    }
    return arr
  }, [format, open, setMachine, setSearchPClss, setSearchSClss, setSearchTag, setUser, states.machine, states.primaryClassifications, states.secondaryClassifications, states.tags, states.user, tStates])
    
  return (
    <>
      <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ p: 2, pl: 4, m: -1}}>
        <SearchNameField />
        <PopUserFilter />
        <PopMachineFilter />
        <PopClassificationFilter />
        <PopTagFilter />
        {PopTimeRangePicker}
      </Stack>
      <Divider />
      <SearchCriteriaChips criteriaChips={criteriaChips} />
    </>
  )
}

export default SecureFilesTableSearchRegion;


const SearchNameField : FC = () => {
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)
  const {
    states: {term},
    searchName,
  } = useSecureFilesTableSearchContext()

  return (
    <TextField 
      id="securefiletable-search-name"
      placeholder='Search by Name'
      fullWidth
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
          </InputAdornment>
        )
      }}
      onChange={(e) => searchName(e.target.value)}
      value={term}
      variant="outlined"
      sx={{ width: 280, mr: 'auto' }}
    />
  )
}


const PopUserFilter = () => {
  const {users} = useFileCataAmbientDataContext()
  const { setUser } = useSecureFilesTableSearchContext()
  return (
    <PopBaseFilter 
      label="User"
      id='user-picker'
      options={users}
      onChange={(event, option) => {
        const user = option
        if (user) {
          setUser(user)
        }
      }}
    />
  )
}

const PopMachineFilter = () => {
  const {machines} = useFileCataAmbientDataContext()
  const { setMachine } = useSecureFilesTableSearchContext()
  return (
    <PopBaseFilter 
      label="Computer"
      id='machine-picker'
      options={machines as any}
      onChange={(event, option) => {
        const machine = option as any as SimpleOption2
        if (machine) {
          setMachine(machine)
        }
      }}
    />
  )
}

const PopClassificationFilter = () => {
  const {classifications} = useFileCataAmbientDataContext()
  const { setSearchSClss } = useSecureFilesTableSearchContext()
  return (
    <PopBaseFilter 
      label="Classification"
      id='classification-picker'
      options={classifications}
      onChange={(event, option) => {
        const clsItem = option as SecondaryClassificationResponseDto
        if (clsItem) {
          setSearchSClss(clsItem, false)
        }
      }}
    />
  )
}

const PopTagFilter = () => {
  const {tags} = useFileCataAmbientDataContext()
  const { setSearchTag } = useSecureFilesTableSearchContext()
  return (
    <PopBaseFilter 
      label="Tag"
      id='tag-picker'
      options={tags}
      onChange={(event, option) => {
        const tag = option as FileTagResponseDto
        if (tag) {
          setSearchTag(tag, false)
        }
      }}
    />
  )
}